import React, { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { ImageAttachmentField } from '@/components/ui/ImageAttachmentField';
import { showToast } from '@/components/ui/Toast';
import { Plus, Pencil, ArrowUp, ArrowDown, Trash2 } from 'lucide-react';
import { t } from '@/lib/i18n';

export interface InlineFieldDef<T> {
  name: keyof T & string;
  label: string;
  type: 'text' | 'textarea' | 'select' | 'image';
  placeholder?: string;
  hint?: string;
  required?: boolean;
  rows?: number;
  options?: ReadonlyArray<{ value: string; label: string }>;
}

export interface InlineEntityListProps<T extends { id: string }> {
  title: string;
  /** Optional subtitle rendered below the section heading. DOGFOOD-6. */
  subtitle?: string;
  emptyHint?: string;
  items: T[];
  fields: ReadonlyArray<InlineFieldDef<T>>;
  renderRow: (item: T) => React.ReactNode;
  onCreate: (draft: Partial<T>) => Promise<void>;
  onUpdate: (id: string, patch: Partial<T>) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onReorder?: (index: number, direction: 'up' | 'down') => Promise<void> | void;
  /** Optional row-level slot rendered after the standard row actions (e.g. "+ Add ICP"). */
  rowActions?: (item: T) => React.ReactNode;
  /** Optional nested content (used for MarketTree to render children under the row). */
  renderExpanded?: (item: T) => React.ReactNode;
  /** Optional heading-level action slot (shown instead of the default Add button). Rare. */
  headerActions?: React.ReactNode;
  addLabel?: string;
  disabled?: boolean;
  /**
   * Read-only mode hides all edit affordances entirely: the Add button,
   * per-row Edit / Reorder / Delete icons, and any caller-provided
   * `rowActions`. Existing rows still render via `renderRow`, so users can
   * still SEE the data — they just can't change it. `disabled` only
   * greys out the buttons; `readOnly` removes them from the DOM.
   */
  readOnly?: boolean;
}

function emptyDraft<T>(fields: ReadonlyArray<InlineFieldDef<T>>): Partial<T> {
  const draft: Record<string, unknown> = {};
  for (const f of fields) draft[f.name] = '';
  return draft as Partial<T>;
}

export function InlineEntityList<T extends { id: string }>(props: InlineEntityListProps<T>) {
  const {
    title, subtitle, emptyHint, items, fields,
    renderRow, onCreate, onUpdate, onDelete, onReorder,
    rowActions, renderExpanded, headerActions, addLabel, disabled, readOnly,
  } = props;

  const [mode, setMode] = useState<
    | { kind: 'idle' }
    | { kind: 'adding'; draft: Partial<T> }
    | { kind: 'editing'; id: string; draft: Partial<T> }
  >({ kind: 'idle' });
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  function startAdd() {
    setMode({ kind: 'adding', draft: emptyDraft(fields) });
  }

  function startEdit(item: T) {
    const draft: Record<string, unknown> = {};
    for (const f of fields) {
      const v = (item as Record<string, unknown>)[f.name];
      draft[f.name] = v == null ? '' : v;
    }
    setMode({ kind: 'editing', id: item.id, draft: draft as Partial<T> });
  }

  function cancel() {
    setMode({ kind: 'idle' });
  }

  function updateDraft(name: string, value: unknown) {
    setMode((m) => {
      if (m.kind === 'idle') return m;
      return { ...m, draft: { ...m.draft, [name]: value } };
    });
  }

  function normalisePayload(draft: Partial<T>): Partial<T> {
    const out: Record<string, unknown> = {};
    for (const f of fields) {
      const raw = (draft as Record<string, unknown>)[f.name];
      if (typeof raw === 'string') {
        const trimmed = raw.trim();
        out[f.name] = trimmed === '' ? null : trimmed;
      } else {
        out[f.name] = raw;
      }
    }
    return out as Partial<T>;
  }

  function validate(draft: Partial<T>): string | null {
    for (const f of fields) {
      if (!f.required) continue;
      const v = (draft as Record<string, unknown>)[f.name];
      if (typeof v !== 'string' || v.trim() === '') {
        return t('{field} is required').replace('{field}', f.label);
      }
    }
    return null;
  }

  async function submit() {
    if (mode.kind === 'idle') return;
    const err = validate(mode.draft);
    if (err) {
      showToast.error(err);
      return;
    }
    const payload = normalisePayload(mode.draft);
    setBusy(true);
    try {
      if (mode.kind === 'adding') {
        await onCreate(payload);
      } else {
        await onUpdate(mode.id, payload);
      }
      setMode({ kind: 'idle' });
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('Something went wrong');
      showToast.error(msg);
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await onDelete(deleteTarget);
      setDeleteTarget(null);
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('Failed to delete');
      showToast.error(msg);
    } finally {
      setBusy(false);
    }
  }

  async function move(index: number, direction: 'up' | 'down') {
    if (!onReorder) return;
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === items.length - 1) return;
    try {
      await onReorder(index, direction);
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('Failed to reorder');
      showToast.error(msg);
    }
  }

  const renderForm = (draft: Partial<T>, submitLabel: string) => {
    const imageFields = fields.filter((f) => f.type === 'image');
    const textFields = fields.filter((f) => f.type !== 'image');

    const renderField = (f: InlineFieldDef<T>) => {
      const value = (draft as Record<string, unknown>)[f.name] ?? '';
      const strValue = typeof value === 'string' ? value : String(value ?? '');
      if (f.type === 'image') {
        const imgValue = typeof value === 'string' && value ? value : null;
        return (
          <ImageAttachmentField
            key={f.name}
            label={f.label}
            hint={f.hint}
            value={imgValue}
            onChange={(id) => updateDraft(f.name, id)}
            layout="vertical"
          />
        );
      }
      if (f.type === 'textarea') {
        return (
          <Textarea
            key={f.name}
            label={f.label}
            value={strValue}
            onChange={(e) => updateDraft(f.name, e.target.value)}
            placeholder={f.placeholder}
            hint={f.hint}
            rows={f.rows ?? 3}
          />
        );
      }
      if (f.type === 'select' && f.options) {
        return (
          <Select
            key={f.name}
            label={f.label}
            hint={f.hint}
            value={strValue || undefined}
            onValueChange={(v) => updateDraft(f.name, v)}
            placeholder={t('— Select —')}
            options={f.options.map((o) => ({ value: o.value, label: o.label }))}
          />
        );
      }
      return (
        <Input
          key={f.name}
          label={f.label}
          value={strValue}
          onChange={(e) => updateDraft(f.name, e.target.value)}
          placeholder={f.placeholder}
          hint={f.hint}
        />
      );
    };

    const actions = (
      <div className="flex gap-2">
        <Button type="button" size="sm" onClick={submit} isLoading={busy}>
          {submitLabel}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={cancel} disabled={busy}>
          {t('Cancel')}
        </Button>
      </div>
    );

    if (imageFields.length === 0) {
      return (
        <div className="space-y-2">
          {textFields.map(renderField)}
          <div className="pt-1">{actions}</div>
        </div>
      );
    }

    // With image fields present: 2-column layout.
    // The right column is height-driven by the square image; the left column
    // matches it via flex-stretch. Both action rows use `mt-auto` so they
    // anchor to the same vertical baseline regardless of the text column's
    // natural height.
    return (
      <div className="flex flex-col md:flex-row gap-4 md:items-stretch">
        <div className="flex-1 flex flex-col min-w-0 gap-2">
          {textFields.map(renderField)}
          <div className="mt-auto">{actions}</div>
        </div>
        <div className="md:w-64">
          {imageFields.map(renderField)}
        </div>
      </div>
    );
  };

  const showEmpty = items.length === 0 && mode.kind !== 'adding';

  return (
    <section className="space-y-3">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          {subtitle && (
            <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>
          )}
        </div>
        {readOnly ? null : (headerActions ?? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            leftIcon={<Plus className="h-3.5 w-3.5" />}
            onClick={startAdd}
            disabled={disabled || mode.kind !== 'idle'}
          >
            {addLabel ?? t('Add')}
          </Button>
        ))}
      </div>

      {mode.kind === 'adding' && (
        <div className="rounded-md border border-border bg-muted/30 p-3">
          {renderForm(mode.draft, t('Save'))}
        </div>
      )}

      {showEmpty ? (
        <p className="text-sm text-muted-foreground italic">
          {emptyHint ?? (readOnly
            ? t('Nothing here yet.')
            : t('Nothing here yet. Click Add to create the first entry.'))}
        </p>
      ) : (
        <div className="space-y-2">
          {items.map((item, index) => {
            const isEditing = mode.kind === 'editing' && mode.id === item.id;
            return (
              <div key={item.id} className="rounded-md border border-border bg-muted/30 p-3">
                {isEditing ? (
                  renderForm(mode.draft, t('Save'))
                ) : (
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">{renderRow(item)}</div>
                    {!readOnly && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => startEdit(item)}
                          disabled={disabled || mode.kind !== 'idle'}
                          title={t('Edit')}
                          aria-label={t('Edit')}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        {onReorder && (
                          <>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => void move(index, 'up')}
                              disabled={disabled || index === 0}
                              title={t('Move up')}
                              aria-label={t('Move up')}
                            >
                              <ArrowUp className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => void move(index, 'down')}
                              disabled={disabled || index === items.length - 1}
                              title={t('Move down')}
                              aria-label={t('Move down')}
                            >
                              <ArrowDown className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => setDeleteTarget(item.id)}
                          disabled={disabled}
                          title={t('Delete')}
                          aria-label={t('Delete')}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                        {rowActions?.(item)}
                      </div>
                    )}
                  </div>
                )}
                {!isEditing && renderExpanded && (
                  <div className="mt-3">{renderExpanded(item)}</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => void confirmDelete()}
        title={t('Delete entry')}
        description={t('This cannot be undone. Continue?')}
        confirmText={t('Delete')}
        variant="destructive"
        isLoading={busy}
      />
    </section>
  );
}
